<template>
  <v-layout grid-list-xl>
    <v-flex>
      <div
        class="v dashboard"
        :class="className"
      >
        <mapbox
          v-if="showMap"
          ref="mapbox"
          :access-token="$store.state.variables.mapbox.accessToken"
          :map-style="$store.state.variables.mapbox.defaultOptions.style"
          :center="center"
          :zoom="overview ? 10 : 16"
          :nodes="mapNodes"
          :overview="overview"
          @endpoint-click="onEndpointClick"
        />
        <grid-areas
          v-if="tvMode === false"
          @item-click="onGridAreaItemClick"
        />
        <endpoint-side-panel
          v-show="!!sidepanelEndpoint.id"
          ref="endpoint"
          :endpoint="sidepanelEndpoint"
        />
      </div>
      <v-flex>
        <tv-side-panel v-if="tvMode" ref="tvSidePanel" />
      </v-flex>
    </v-flex>
  </v-layout>
</template>

<script>
import * as turf from '@turf/turf';

import GridAreas from '../components/Dashboard/GridAreas.vue';
import EndpointSidePanel from '../components/EndpointSidePanel.vue';
import TvSidePanel from '../components/TvSidePanel.vue';

const Mapbox = () => import(/* webpackChunkName: "mapbox_component" */ '../components/Mapbox.vue');

export default {
  name: 'Dashboard',
  components: {
    Mapbox,
    GridAreas,
    EndpointSidePanel,
    TvSidePanel,
  },
  props: {
    tvMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMap: true,
      overview: false,
      mapNodes: [],
      sidepanelEndpoint: {},
      className: '',
    };
  },
  computed: {
    zoom() {
      if (this.overview) {
        return 10;
      }
      return 16;
    },
    areas() {
      return this.$store.state.gridAreas.areas;
    },
    currentIndex() {
      return this.$store.state.gridAreas.showIndex;
    },
    center() {
      return this.$store.state.gridAreas.mapCenter;
    },
    disorderAreaId() {
      return this.$store.state.gridAreas.disorderAreaId;
    },
    disorderShowMap() {
      return this.$store.state.gridAreas.disorderShowMap;
    },
    navigationTabs() {
      return this.$store.state.navigation.tabs;
    },
  },

  watch: {
    disorderAreaId(newVal) {
      if (newVal !== null) {
        this.onDisorder();
        if (newVal !== -1 && newVal !== -2 && !this.tvMode) {
          this.$router.push(`/area/${newVal}`);
          this.mapNodes = this.$store.state.gridAreas.disorderAreaNodes;
        }
        if (newVal === -2 && this.$route.name === 'dashboard') {
          this.mapNodes = [];
          this.overview = true;
        }
        if (this.tvMode) {
          this.$store.dispatch('GET_ALL_EVENTS', false);
          this.$store.dispatch('GET_STATS');
          this.setOverview(true);
        }
        this.$store.state.gridAreas.disorderAreaId = null;
      }
    },
    areas: {
      immediate: true,
      handler(newVal) {
        if (newVal.length === 0) {
          this.$store.dispatch('GET_ALL_GRID_AREAS');
        } else if ((newVal.length > 0 && this.$route.name === 'dashboard') || this.tvMode) {
          this.setOverview(true);
        }
      },
    },
    $route: {
      immediate: true,
      handler(to, from) {
        if (this.$route.name === 'dashboard-area-detail' && (from === undefined || from.name !== 'dashboard')) {
          this.$store.commit('SET_NAV_RIGHT_ELEMENTS', null);
          if (this.areas.length === 0) {
            this.showMap = false;
            this.$store.dispatch('GET_ALL_GRID_AREAS', true)
              .then(() => {
                this.showDetailView();
                this.$store.dispatch('GET_ALL_EVENTS', true);
              });
          } else {
            this.showDetailView();
            this.$store.dispatch('GET_ALL_EVENTS', true);
          }
          this.$store.commit('SET_NAV_TABS', null);
        } else if (this.$route.name === 'dashboard-area-detail' && from.name === 'dashboard') {
          this.$store.commit('SET_NAV_RIGHT_ELEMENTS', null);
          if (this.areas.length === 0) {
            this.showMap = false;
            this.$store.dispatch('GET_ALL_GRID_AREAS', true)
              .then(() => {
                this.showDetailView();
              });
          } else {
            this.showDetailView();
          }
          this.$store.commit('SET_NAV_TABS', null);
        } else if (from !== undefined && from.name !== 'dashboard') {
          this.setOverview(true);
          this.$store.commit('SET_NAV_TITLE', 'Grid Monitoring Light');
          this.$store.commit('SET_NAV_TABS', [
            { label: 'Karte', to: '/' },
            { label: 'Ereignisse', to: '/events' },
          ]);
          this.$store.commit('SET_NAV_RIGHT_ELEMENTS', [
            {
              flat: true, to: '/settings', icon: { name: 'settings', show: true },
            },
            {
              flat: true, to: '/tv', icon: { name: 'tv', show: true },
            },
          ]);
        } else {
          this.setOverview(true);
          this.$store.dispatch('GET_ALL_EVENTS');
          this.$store.dispatch('GET_ALL_GRID_AREAS', true);
          this.$store.commit('SET_NAV_TITLE', 'Grid Monitoring Light');
          this.$store.commit('SET_NAV_TABS', [
            { label: 'Karte', to: '/' },
            { label: 'Ereignisse', to: '/events' },
          ]);
          this.$store.commit('SET_NAV_RIGHT_ELEMENTS', [
            {
              flat: true, to: '/settings', icon: { name: 'settings', show: true },
            },
            {
              flat: true, to: '/tv', icon: { name: 'tv', show: true },
            },
          ]);
        }
      },
    },
    navigationTabs: {
      immediate: true,
      handler(newVal) {
        if (newVal !== null) {
          this.className = 'view-has-tabs';
        } else {
          this.className = '';
        }
      },
    },
  },
  mounted() {
    this.$store.commit('SET_NAV_TITLE', 'Grid Monitoring Light');
    this.$store.commit('SET_NAV_COLOR', 'white');
    this.$store.commit('SET_NAV_CLASS', 'is-transparent');
    this.$store.dispatch('SET_EVENT_WEBSOCKET_CONNECTION');
    this.$store.dispatch('HANDLE_DESKTOP_NOTIFICATIONS');
  },
  methods: {
    setOverview(bool) {
      this.overview = bool;
      if (bool && this.$store.state.gridAreas.areas.length > 1) {
        const coordsArr = [];
        const pointsArr = [];
        this.$store.state.gridAreas.areas.map((area) => {
          coordsArr.push(area.position.coordinates);
          pointsArr.push(turf.point(area.position.coordinates));
        });
        const lineString = turf.lineString(coordsArr);
        const bbox = turf.bbox(lineString);
        const featureCollection = turf.featureCollection(pointsArr);
        const center = turf.center(featureCollection);
        this.$store.state.gridAreas.mapCenter = center.geometry.coordinates;
        this.$store.state.gridAreas.showIndex = null;
        this.$store.state.gridAreas.shownArea = null;
        setTimeout(() => {
          this.$refs.mapbox.map.fitBounds(bbox, {
            padding: 128,
          });
          this.$refs.mapbox.removeAllSources();
        }, 500);
      } else if (bool && this.$store.state.gridAreas.areas.length <= 1) {
        this.$store.state.gridAreas.mapCenter = [9.662680, 54.237812];
      }
    },
    onGridAreaItemClick(area) {
      setTimeout(() => {
        this.overview = false;
        this.$store.state.gridAreas.mapCenter = area.center;
        this.$store.commit('SET_SHOWN_AREA', area.id);
        this.showMap = false;
        this.$router.push(`/area/${area.id}`);
        // @TODO: Review - kein plan was hier passiert
        setTimeout(() => {
          this.showMap = true;
        }, 200);
        setTimeout(() => {
          this.$refs.mapbox.setNodes(this.$refs.mapbox.map);
        }, 400);
      }, 1);
    },
    onDisorder() {
      this.showMap = false;
      setTimeout(() => {
        this.showMap = true;
        this.$nextTick(() => {
          this.$refs.mapbox.setNodes(this.$refs.mapbox.map);
          if (!this.overview) {
            this.$refs.mapbox.calculateBbox();
          }
        });
      }, 500);
    },
    onEndpointClick(endpoint) {
      this.sidepanelEndpoint = endpoint;
      this.$store.dispatch('GET_ENDPOINT_DETAILS_BY_ID', endpoint);
      this.$refs.endpoint.toggle();
    },
    showDetailView() {
      this.$store.state.gridAreas.areas.map((area) => {
        if (area.id === Number(this.$route.params.id)) {
          this.mapNodes = area.endpoints;
          this.$nextTick(() => {
            this.onGridAreaItemClick(area);
            this.$store.commit('SET_NAV_TITLE', area.label);
          });
          this.showMap = true;
        }
      });
    },
  },
};
</script>
